import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { MapPin, Plus } from 'lucide-react';
import { CarrierHubDialog } from './CarrierHubDialog';
import { AppDispatch } from '@/redux/store';
import { useDispatch } from 'react-redux';
import { doCreateCarrierHub } from '../../../modules/carriers/stores/thunk';
import DefaultMapbox from '../../Mapbox/DefaultMapbox';

interface CarrierHub {
  id: number;
  name: string;
  address: string;
  addressObject: any;
  addressLat: number;
  addressLng: number;
}

interface CarrierHubsSectionProps {
  carrierId: number;
  hubs: CarrierHub[];
}

export const CarrierHubsSection: React.FC<CarrierHubsSectionProps> = ({ carrierId, hubs = [] }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const handleCreateHub = async (data: any) => {
    await dispatch(doCreateCarrierHub(data));
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Carrier Hubs ({hubs.length})
            </CardTitle>
            <Button onClick={() => setDialogOpen(true)} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Hub
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {hubs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {hubs.map((hub) => (
                <Card key={hub.id} className="overflow-hidden">
                  <CardContent className="p-0">
                    {/* Map Section */}
                    <div className="h-32 relative">
                      <DefaultMapbox
                        markers={[
                          {
                            content: (
                              <div className="flex items-center justify-center">
                                <MapPin className="h-6 w-6 text-blue-500 drop-shadow-lg" />
                              </div>
                            ),
                            latitude: hub.addressLat,
                            longitude: hub.addressLng,
                            offsetX: 0,
                            offsetY: -12,
                          },
                        ]}
                        className="w-full h-full"
                        isControl={false}
                        lngLatMove={[hub.addressLng, hub.addressLat]}
                      />
                    </div>

                    {/* Hub Info Section */}
                    <div className="p-4">
                      <div className="flex items-start gap-3">
                        <MapPin className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <h4 className="font-medium text-sm mb-1 line-clamp-1">{hub.name}</h4>
                          <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{hub.address}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No hubs configured</h3>
              <p className="text-muted-foreground mb-4">This carrier has no hub locations configured</p>
              <Button onClick={() => setDialogOpen(true)} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Add First Hub
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <CarrierHubDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        carrierId={carrierId}
        onSubmit={handleCreateHub}
      />
    </>
  );
};
